/* Live jobs: your staffed jobs that are under way or about to be. A job that
 * still needs someone is on Assign staff instead; one that has finished leaves
 * the list (it shows on Earnings once it pays).
 *
 * Who is on each job comes from the job itself (GET /agency/items/{id}): the
 * list carries no names. Each open is recorded as a view of the patient's
 * details, the same as opening it on Assign staff. Re-read every 30 seconds. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getItem, listItems, type ItemDetail, type ItemSummary } from '../data/agency';
import { dateOnly, dateTime } from '../data/format';
import { jobName, roleName } from '../data/words';
import { AllClear, Card, CardHead, CellStack, Empty, Kpi, KpiGrid, Note, Stack, Table, TRow, uiCss } from '../ui';
import { ItemStatusTag } from './assign';
import { ServiceDot, useAsync } from './common';

const COLS = '1.5fr 1fr 1.1fr 1.6fr 1.2fr 0.9fr';

type Focus = 'all' | 'now' | 'next' | 'gap';

export interface LiveRow {
  summary: ItemSummary;
  detail: ItemDetail | null;
}

export async function readLive(): Promise<LiveRow[]> {
  const items = await listItems();
  const live = items.filter((r) => r.status === 'ASSIGNED' || r.status === 'IN_PROGRESS');
  const details = await Promise.all(live.map((r) => getItem(r.public_id).catch(() => null)));
  return live.map((summary, i) => ({ summary, detail: details[i] ?? null }));
}

/** Who is on the job now: the people at work first (checked in), then the ones
 *  accepted or waiting to accept. */
export function onIt(d: ItemDetail | null): { now: string[]; next: string[]; since: string | null } {
  if (!d) return { now: [], next: [], since: null };
  const active = d.assignments.filter((a) => a.status === 'ACTIVE');
  const waiting = d.assignments.filter((a) => a.status === 'ACCEPTED' || a.status === 'PROPOSED');
  const since = active
    .map((a) => a.check_in?.at_utc ?? a.started_at_utc)
    .filter((x): x is string => !!x)
    .sort()[0] ?? null;
  return {
    now: Array.from(new Set(active.map((a) => `${a.resource_label} (${roleName(a.role, d.service_code).toLowerCase()})`))),
    next: Array.from(new Set(waiting.map((a) => a.resource_label))),
    since,
  };
}

export function LiveScreen() {
  const navigate = useNavigate();
  const data = useAsync(readLive, [], 30_000);
  const [focus, setFocus] = useState<Focus>('all');
  const rows = useMemo(() => data.data ?? [], [data.data]);

  const counts = {
    all: rows.length,
    now: rows.filter((r) => r.summary.status === 'IN_PROGRESS').length,
    next: rows.filter((r) => r.summary.status === 'ASSIGNED').length,
    gap: rows.filter((r) => r.summary.coverage.next_uncovered_shift_at !== null).length,
  };
  const shown = rows.filter((r) =>
    focus === 'now'
      ? r.summary.status === 'IN_PROGRESS'
      : focus === 'next'
        ? r.summary.status === 'ASSIGNED'
        : focus === 'gap'
          ? r.summary.coverage.next_uncovered_shift_at !== null
          : true,
  );
  const pick = (f: Focus) => setFocus((cur) => (cur === f && f !== 'all' ? 'all' : f));
  const tone = (f: Focus) => (focus === f ? { bg: 'var(--teal-bg)', bd: 'var(--teal)', c: 'var(--teal)' } : undefined);

  return (
    <Stack>
      <KpiGrid>
        <Kpi label="Staffed jobs" value={counts.all} note="under way or booked next" onClick={() => pick('all')} tone={tone('all')} />
        <Kpi
          label="Happening now"
          value={counts.now}
          note="someone has checked in"
          color={counts.now ? 'var(--ok)' : undefined}
          onClick={() => pick('now')}
          tone={tone('now')}
        />
        <Kpi label="Coming up" value={counts.next} note="staffed, not started" onClick={() => pick('next')} tone={tone('next')} />
        <Kpi
          label="With a gap ahead"
          value={counts.gap}
          note="a later shift has nobody"
          color={counts.gap ? 'var(--amber)' : undefined}
          onClick={() => pick('gap')}
          tone={tone('gap')}
        />
      </KpiGrid>

      <Card>
        <CardHead title="Your people at work" sub="Open a job to see its shifts, who checked in and when, or to change who goes." />
        {data.error ? (
          <div style={{ padding: '0 18px 18px' }}>
            <Note tone="danger">Your live jobs could not be read: {data.error}</Note>
          </div>
        ) : data.loading && rows.length === 0 ? (
          <Empty>Reading your live jobs…</Empty>
        ) : shown.length === 0 ? (
          rows.length === 0 ? (
            <AllClear title="Nobody is on a job" sub="Jobs appear here once someone is assigned. Jobs that still need someone are on Assign staff." />
          ) : (
            <Empty>Nothing matches this filter. Click the tile again to see every job.</Empty>
          )
        ) : (
          <Table cols={COLS} head={['Job', 'Area', 'Dates', 'Who is on it', 'Covered until', 'Status']} minWidth={900}>
            {shown.map(({ summary: r, detail }) => {
              const who = onIt(detail);
              return (
                <TRow key={r.public_id} cols={COLS} onClick={() => navigate(`/assign/${encodeURIComponent(r.public_id)}`)}>
                  <CellStack
                    top={
                      <span style={{ font: '700 13.5px var(--font-ui)', color: 'var(--ink)' }}>
                        <ServiceDot code={r.service_code} />
                        {jobName(r.service_code, r.variant_code)}
                      </span>
                    }
                    sub={<span className={uiCss.mono}>{r.public_id}</span>}
                  />
                  <span className={uiCss.sub}>{[r.locality.pincode, r.locality.city_code].filter(Boolean).join(' · ') || '—'}</span>
                  <span>
                    {r.start_date ? dateOnly(r.start_date) + (r.end_date && r.end_date !== r.start_date ? ` – ${dateOnly(r.end_date)}` : '') : '—'}
                  </span>
                  {detail ? (
                    <CellStack
                      top={who.now.length ? who.now.join(', ') : who.next.length ? who.next.join(', ') : '—'}
                      sub={
                        who.now.length
                          ? who.since
                            ? `checked in ${dateTime(who.since)}`
                            : 'at work'
                          : who.next.length
                            ? 'booked, not checked in yet'
                            : undefined
                      }
                    />
                  ) : (
                    <span className={uiCss.sub}>Could not be read</span>
                  )}
                  <CellStack
                    top={dateTime(r.coverage.assigned_until)}
                    sub={r.coverage.next_uncovered_shift_at ? `Gap from ${dateTime(r.coverage.next_uncovered_shift_at)}` : undefined}
                  />
                  <span>
                    <ItemStatusTag status={r.status} />
                  </span>
                </TRow>
              );
            })}
          </Table>
        )}
      </Card>
    </Stack>
  );
}
